/**
 * 品牌与全局视觉配置（与 VUE_APP_HOME_THEME 一一对应）
 *
 * 作用范围：
 * - Header.vue：顶栏 logo、slogan 布局、可选整幅顶栏背景图（header.*）
 * - Footer.vue：页脚主图/制作方图、是否显示红字标签、云南日报专用版式（footer.variant）
 * - 登录等页：logos.auth 等
 *
 * 约定：
 * - 除首页可由 themes 单独换版式外，全站共用同一套 Header/Footer 组件；换品牌主要靠本文件 + public/home/<theme>/ 静态资源。
 * - slogan 非空时 Header 使用「logo + 标语」并排（header--with-slogan），见 Header.vue。
 * - getBranding() 在构建时随 THEME_KEY 固定，修改主题需重新构建并保证环境变量一致。
 */

/** 当前构建使用的主题键，仅来自环境变量；未配置时为空字符串（不默认黑龙江日报） */
const THEME_KEY = String(process.env.VUE_APP_HOME_THEME || "").trim().toLowerCase();

const BRANDING_MAP = {
  /**
   * 中国青年杂志
   */
  zgqnb: {
    sysName: "中国青年杂志",
    paperMainName: "中国青年杂志",
    slogan: "",
    searchFilters: {
      paperName: false,
      jointLayout: true,
    },
    logos: {
      header: "/home/zgqnb/home/logo_white.png",
      auth: "/home/zgqnb/home/logo.png",
      footerMain: "",
      footerMaker: "",
    },
    footer: {
      labelText: "",
      showMainLabel: false,
      showMaker: true,
      copyrightText: "中国青年杂志社 版权所有",
      bg: "#f5f5f5",
      spanColor: "#666666",
      labelColor: "#c8102e",
      labelMargin: "0 10px 0 0",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * 法治日报
   */
  fzrb: {
    sysName: "法治日报",
    paperMainName: "法治日报",
    slogan: "",
    searchFilters: {
      paperName: true,
      jointLayout: true,
    },
    logos: {
      header: "/home/fzrb/home/paper_logo_write.png",
      auth: "/home/fzrb/home/paper_login.png",
      footerMain: "/home/fzrb/home/logo.png",
      footerMaker: "",
    },
    footer: {
      labelText: "",
      showMainLabel: false,
      showMaker: false,
      hideInlineCopyright: true,
      bg: "#f0f0f0",
      spanColor: "#787878",
      labelColor: "#e60012",
      labelMargin: "0 10px 0 0",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * 中国环境报（zghjb）：对齐 zghjb_web 全站品牌
   * - logo 均来自 public/home/zghjb/common/，已从 zghjb_web/src/assets/ 对应目录复制
   * - 页脚结构与 grrb/hljrb 一致：小 logo + 「社」红字标签 + 青苹果制作 logo
   */
  zghjb: {
    sysName: "中国环境报",
    paperMainName: "中国环境报",
    slogan: "",
    searchFilters: {
      paperName: false,
      jointLayout: true,
    },
    logos: {
      header: "/home/zghjb/common/logo_write.png",
      auth: "/home/zghjb/home/logo.png",
      footerMain: "",
      footerMaker: "/home/zghjb/common/logo_qpg.png",
    },
    footer: {
      labelText: "",
      showMainLabel: false,
      showMaker: true,
      copyrightText: "中国环境报社 版权所有",
      bg: "#dcdcdc",
      spanColor: "#000000",
      labelColor: "#e60012",
      labelMargin: "0 10px 0 0",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * 黑龙江日报：顶栏白字单 logo，无标语；页脚小图 + 报业集团 + 制作 logo
   */
  hljrb: {
    sysName: "黑龙江日报",
    paperMainName: "黑龙江日报",
    slogan: "",
    /**
     * 检索筛选项显示控制（全站统一、按主题生效）
     * - paperName：是否显示“报纸名称”筛选
     * - jointLayout：是否显示“版面类型（正常/连版通版）”筛选
     */
    searchFilters: {
      paperName: true,
      jointLayout: true,
    },
    logos: {
      header: "/home/hljrb/common/logo_white.png",
      auth: "/home/hljrb/common/logo.png",
      footerMain: "/home/hljrb/common/logo_small.png",
      footerMaker: "/home/hljrb/common/logo_qpg.png",
    },
    footer: {
      labelText: "报业集团",
      showMaker: true,
      bg: "#e5e5e5",
      spanColor: "#494949",
      labelColor: "#e60012",
      labelMargin: "0 3px 0 2px",
      mobileLogoHeightPx: 19,
    },
  },
  /**
   * 工人日报：顶栏 logo + 标语「全文检索数据库」；页脚红字为「社」，不展示制作方图
   */
  grrb: {
    sysName: "工人日报",
    paperMainName: "工人日报",
    slogan: "全文检索数据库",
    // grrb 需求：隐藏“报纸名称”“版面类型”
    searchFilters: {
      paperName: false,
      jointLayout: false,
    },
    logos: {
      header: "/home/grrb/common/logo_white.png",
      auth: "/home/grrb/common/logo.png",
      footerMain: "/home/grrb/common/logo_small.png",
      footerMaker: "/home/grrb/common/logo_qpg.png",
    },
    footer: {
      labelText: "社",
      showMaker: false,
      bg: "#f0f0f0",
      spanColor: "#787878",
      labelColor: "#ea0000",
      labelMargin: "0 10px 0 0",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * 云南日报（ynrb）
   *
   * 与独立 ynrb 项目对齐的差异点：
   * - header：整幅 bg_head.png 铺顶栏（非默认 $mainColor 纯色），由 Header.vue 读取 header.backgroundImage 等字段
   * - logos.footerMain / footerMaker：原站为 footer_01 / footer_02 两张长条图，非「小 logo + 制作」的通用版式
   * - footer.variant === 'ynrb' 时 Footer.vue 启用分割线、隐藏红字标签、调整字号与间距（footer--ynrb）
   */
  ynrb: {
    sysName: "云南日报",
    paperMainName: "云南日报",
    slogan: "",
    // ynrb 默认与 grrb 一致：隐藏“报纸名称”“版面类型”
    searchFilters: {
      paperName: false,
      jointLayout: false,
    },
    /**
     * 顶栏背景（可选）：若配置 backgroundImage，Header 将追加 header--image-bg，并以内联样式覆盖默认红底
     * @property {string} backgroundImage - public 下的绝对 URL 路径
     * @property {string} [backgroundSize] - 默认 cover
     * @property {string} [backgroundPosition] - 默认 center center
     */
    header: {
      backgroundImage: "/home/ynrb/common/bg_head.png",
      backgroundSize: "cover",
      backgroundPosition: "center center",
    },
    logos: {
      header: "/home/ynrb/common/logo_white.png",
      auth: "/home/ynrb/common/logo.png",
      footerMain: "/home/ynrb/common/footer_01.png",
      footerMaker: "/home/ynrb/common/footer_02.png",
    },
    /**
     * @property {string} [variant] - 'ynrb' 时启用 Footer 云南专用模板类
     * @property {boolean} [topDivider] - 是否显示顶部分割线（未传且 variant 为 ynrb 时 Footer 内也会按 ynrb 处理）
     * @property {boolean} [showMainLabel] - 是否显示「报业集团」类红字（云南日报为 false）
     * @property {boolean} [showMaker] - 是否显示第二张图及「制作」文案
     */
    footer: {
      variant: "ynrb",
      topDivider: true,
      showMainLabel: false,
      labelText: "",
      showMaker: true,
      bg: "#ffffff",
      spanColor: "#afafaf",
      labelColor: "#e60012",
      labelMargin: "0 3px 0 2px",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * 文汇新民（whb）
   * - logos.header：全站顶栏紧凑图（common/logo_header）
   * - logos.auth：登录/修改密码/留言等页顶图，与首页顶栏条图同一资源 home/header_brand.png（命名与 HomeTheme 中 headerBrand 一致）
   * - 页脚：home/footer_strip
   */
  whb: {
    sysName: "上海报业集团",
    paperMainName: "上海报业集团",
    slogan: "",
    searchFilters: {
      paperName: true,
      jointLayout: true,
    },
    logos: {
      header: "/home/whb/common/logo_header.png",
      auth: "/home/whb/home/header_brand.png",
      footerMain: "/home/whb/home/footer_strip.png",
      footerMaker: "",
    },
    footer: {
      variant: "whb",
      hideInlineCopyright: true,
      showMainLabel: false,
      showMaker: false,
      bg: "#ffffff",
      spanColor: "#494949",
      labelColor: "#e60012",
      labelMargin: "0",
      mobileLogoHeightPx: 24,
    },
    defaultDateSort: "ASC",
  },
  /**
   * 上海支部生活（shzbsh）
   */
  shzbsh: {
    sysName: "上海支部生活",
    paperMainName: "上海支部生活",
    slogan: "",
    searchFilters: {
      paperName: false,
      jointLayout: false,
    },
    logos: {
      header: "/home/shzbsh/home/header_logo.png",
      homeBrand: "/home/shzbsh/common/logo_header.png",
      auth: "/home/shzbsh/home/auth_login.png",
      footerMain: "/home/shzbsh/home/footer_strip.png", // 頁脚圖片
      footerMaker: "",
    },
    footer: {
      variant: "shzbsh",
      hideInlineCopyright: true,
      showMainLabel: false,
      showMaker: false,
      bg: "#ffffff",
      spanColor: "#494949",
      labelColor: "#e60012",
      labelMargin: "0",
      mobileLogoHeightPx: 24,
    },
  },
  /**
   * 青苹果（qpg）
   */
  qpg: {
    sysName: "青苹果",
    paperMainName: "青苹果",
    slogan: "",
    searchFilters: {
      paperName: true,
      jointLayout: true,
    },
    logos: {
      header: "/home/qpg/home/logo-white.png",
      auth: "/home/qpg/home/logo.png",
      footerMain: "",
      footerMaker: "",
    },
    footer: {
      showMainLabel: false,
      showMaker: false,
      copyrightText: "青苹果中心制作",
      bg: "#f0f0f0",
      spanColor: "#787878",
      labelColor: "#e60012",
      labelMargin: "0 10px 0 0",
      mobileLogoHeightPx: 20,
    },
  },
  /**
   * eastview
   */
  eastview: {
    sysName: "青苹果阅读平台",
    paperMainName: "eastview",
    slogan: "青苹果阅读平台",
    // eastview 主题：结果网格每行放置 6 个子元素（同时驱动默认分页大小与分页选项）
    pageColumns: 6,
    searchFilters: {
      paperName: false,
      jointLayout: false,
    },
    logos: {
      header: "/home/eastview/home/header_logo.png",
      homeBrand: "/home/eastview/home/header_logo.png",
      auth: "/home/qpg/home/logo.png",
      footerMain: "/home/eastview/common/logo.png", // 頁脚圖片
      footerMaker: "",
      browserIcon: "/home/eastview/common/logo.png",
    },
    footer: {
      variant: "eastview",
      hideInlineCopyright: true,
      showMainLabel: false,
      showMaker: false,
      bg: "#ffffff",
      spanColor: "#494949",
      labelColor: "#e60012",
      labelMargin: "0",
      mobileLogoHeightPx: 24,
    },
    config: {
      forbidCopy: true, // 禁止复制
    },
    hasAccountCenter: {
      visible: true,
      isShowAccountCenter: false
    }
  },
  /**
   * 求是（qiushi）—— 派生自 eastview
   *
   * 重要：eastview 配置服务于 eastview 客户，本块专供求是客户，二者互不影响。
   *
   * 与 eastview 的差异：
   * - slogan 为「数据库」（设计稿 31:169 红色品牌文字）
   * - 登录页用 variant: "qiushi" 皮肤（对齐设计稿「登录-确认」）
   * - 资源目录为 public/home/qiushi/
   *
   * 与 eastview 相同（继承基线）：
   * - pageColumns: 6、searchFilters、内页检索组件、首页（一期复用）
   */
  qiushi: {
    /**
     * 派生来源：声明"复用 eastview 的行为逻辑"，供 matchesTheme() 判断（见 config.js）
     * 不加这个字段，qiushi 会渲染出默认 Header + 默认页脚，与 eastview 不一致
     */
    baseTheme: "eastview",
    sysName: "求是",
    paperMainName: "qiushi",
    slogan: "数据库",
    // 结果网格每行放置 6 个子元素（同时驱动默认分页大小与分页选项）
    pageColumns: 6,
    searchFilters: {
      paperName: false,
      jointLayout: false,
    },
    logos: {
      header: "/home/qiushi/home/header_logo.png",
      homeBrand: "/home/qiushi/home/header_logo.png",
      auth: "/home/qiushi/common/logo.png",
      footerMain: "/home/qiushi/common/logo.png",
      footerMaker: "",
      browserIcon: "/home/qiushi/common/favicon.png",
    },
    footer: {
      hideInlineCopyright: true,
      showMainLabel: false,
      showMaker: false,
      bg: "#ffffff",
      spanColor: "#494949",
      labelColor: "#e60012",
      labelMargin: "0",
      mobileLogoHeightPx: 24,
    },
    /**
     * 登录页皮肤（对齐设计稿「登录-确认」）
     * variant 用「起源客户名」而非版本号 —— 与项目既有 footer.variant 约定一致
     * @property {string} variant - 版式 ID，"qiushi" → 类名 .login--qiushi
     */
    login: {
      variant: "qiushi",
    },
    config: {
      forbidCopy: true, // 禁止复制
    },
    hasAccountCenter: {
      visible: true,
      isShowAccountCenter: true
    }, // 是否启用「账号中心」入口（登录后右上角）
  },
};
/**
 * 未配置或未知主题键时的中性品牌（避免出现黑龙江日报等任一报社的默认误显）
 */
const NEUTRAL_BRANDING = {
  sysName: "全文检索",
  paperMainName: "",
  slogan: "",
  searchFilters: {
    paperName: true,
    jointLayout: true,
  },
  logos: {
    header: "/home/hljrb/common/logo_white.png",
    auth: "/home/hljrb/common/logo.png",
    footerMain: "/home/hljrb/common/logo_small.png",
    footerMaker: "/home/hljrb/common/logo_qpg.png",
  },
  footer: {
    labelText: "",
    showMaker: false,
    bg: "#e5e5e5",
    spanColor: "#494949",
    labelColor: "#e60012",
    labelMargin: "0",
    mobileLogoHeightPx: 19,
  },
};

/** @returns {string} 当前主题键，与 THEME_KEY 一致（未配置时为空字符串） */
export function getThemeKey() {
  return THEME_KEY;
}

/**
 * 取当前主题完整配置对象；未配置或未知键时回退中性配置，不默认黑龙江日报
 * @returns {typeof BRANDING_MAP[keyof typeof BRANDING_MAP] | typeof NEUTRAL_BRANDING}
 */
export function getBranding() {
  if (THEME_KEY && BRANDING_MAP[THEME_KEY]) {
    return BRANDING_MAP[THEME_KEY];
  }
  return NEUTRAL_BRANDING;
}

/**
 * 取日期排序默认值。
 * - 各主题可配置 defaultDateSort: "ASC" | "DESC"
 * - 未配置时默认 "DESC"（降序，新日期在前）
 * @returns {"ASC"|"DESC"}
 */
export function getDefaultDateSort() {
  const brand = getBranding();
  return brand.defaultDateSort || "ASC";
}
